import React, { FC, useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";

import axiosInstant from "../../../../api";
import { publicCreateComment, publicApproveFile, publicRequestEditFile } from "../../../../api/endpoints";
import { VideoFeedbackProvider } from "../../../videoProduction/components/videoReview/useVideoFeedback";
import { VideoFeedback } from "../../../videoProduction/components/videoReview/VideoFeedback";
import {
    ConfirmationProps,
    IComment,
    REVIEW_LOCKED_ERROR,
    SubmitFeedbackDialogProps
} from "../../../videoProduction/components/videoReview/constants";
import { apiErrorCode } from "../../../../utils/apiError";
import { EmptyPanel } from "../../../brand/projects/common/EmptyPanel";
import { IconVideo } from "../../../../utils/icons";
import { filterRealFiles } from "../../../videoProduction/helper";
import { PublicProject, PublicProjectFile, PublicProjectFileComment } from "../OrderPublicPage";
import { useChannelGroups } from "../../../../hooks/useChannelGroups";
import { ChannelAccordion } from "../../../videoProduction/components/shared/ChannelAccordion";
import { fileToIVideo } from "../../../../utils/downloadHelpers";

interface Props {
    project: PublicProject;
    token: string;
    onRefresh: () => void;
    publicBase?: string;
    /** Brand file-type allowlist for the review header's Download button. */
    allowedFileTypes?: Set<string> | null;
}

function mapFileComments(file: PublicProjectFile | undefined): IComment[] {
    return (file?.comments ?? []).map((c: PublicProjectFileComment) => ({
        id: String(c.id),
        message: c.message,
        timestamp: c.timestamp,
        timestamp_end: c.timestamp_end,
        created_at: c.created_at,
        user: { userid: 0, displayName: c.guest_name || "User" }
    }));
}

// The whole file passes through (key, hash, bucket, aspect ratio, length,
// option links…): downloads presign with the same friendly filename as the
// project page, and Thumbnails / Transcripts / All content get real keys.
function adaptVideoForProvider(file: PublicProjectFile) {
    return {
        ...fileToIVideo({ ...file, location: file.downloadUrl || file.location }),
        type: file.type,
        review: file.review,
        final: file.approved,
    };
}

interface SingleFileReviewProps {
    file: PublicProjectFile;
    project: PublicProject;
    token: string;
    onRefresh: () => void;
    allFiles: PublicProjectFile[];
    publicBase?: string;
    allowedFileTypes?: Set<string> | null;
}

const noop = () => {};

const SingleFileReview: FC<SingleFileReviewProps> = ({
    file,
    project,
    token,
    onRefresh,
    allFiles,
    publicBase = "brand-orders",
    allowedFileTypes,
}) => {
    const [confirmationData, setConfirmationData] = useState<ConfirmationProps>();
    const [guestName, setGuestName] = useState("");

    // Stable identities matter here: VideoFeedback reloads the <video> element
    // and refetches comments when the video prop identity changes, so rebuilding
    // these per render (e.g. on every guest-name keystroke) resets playback and
    // wipes locally appended comments.
    const adaptedProject: any = useMemo(
        () => ({
            id: project.id,
            title: project.title,
            status: project.status,
            projectFiles: allFiles.map((f) => ({
                ...f,
                location: f.downloadUrl || f.location,
                final: f.approved,
            })),
            // The brand (with pre_approval_downloading): the project share
            // endpoint attaches it to the project; the order page copies the
            // order's brand onto each project before rendering.
            brands: (project as any).brand ? [(project as any).brand] : [],
        }),
        [project.id, project.title, project.status, allFiles, (project as any).brand]
    );

    const adaptedVideo = useMemo(() => adaptVideoForProvider(file), [file]);
    const initialComments = useMemo(() => mapFileComments(file), [file]);

    const createCommentFn = useCallback(
        async (comment: IComment): Promise<IComment | void> => {
            try {
                const { data } = await axiosInstant.post(publicCreateComment(token, project.id, publicBase), {
                    text: comment.message,
                    fileId: file.id,
                    timestamp: comment.timestamp,
                    timestamp_end: comment.timestamp_end,
                    name: guestName.trim() || undefined,
                });
                return {
                    id: data.id ? String(data.id) : String(Date.now()),
                    message: comment.message,
                    timestamp: comment.timestamp,
                    timestamp_end: comment.timestamp_end,
                    created_at: data.created_at,
                    user: { userid: 0, displayName: data.guest_name || "User" },
                };
            } catch (err: any) {
                // Rethrow so CreateCommentPanel's catch toasts AND keeps the
                // guest's draft — swallowing here would run its success path.
                if (apiErrorCode(err) === "FeedbackAlreadySubmitted") {
                    throw new Error(REVIEW_LOCKED_ERROR);
                }
                throw new Error(err?.response?.data?.error || "Failed to add comment. Please try again.");
            }
        },
        [token, project.id, file.id, guestName, publicBase]
    );

    const getCommentsFn = useCallback(async (): Promise<IComment[] | void> => mapFileComments(file), [file]);

    const onApproveFn = useCallback(
        async (fileId: number): Promise<any> => {
            const { data } = await axiosInstant.patch(publicApproveFile(token, project.id, fileId, publicBase));
            return data;
        },
        [token, project.id, publicBase]
    );

    const onRequestEditFn = useCallback(
        async (fileId: number): Promise<any> => {
            const { data } = await axiosInstant.patch(publicRequestEditFile(token, project.id, fileId, publicBase));
            return data;
        },
        [token, project.id, publicBase]
    );

    const onFeedbackSubmit = useCallback(
        () =>
            setConfirmationData({
                ...SubmitFeedbackDialogProps(8),
                onConfirm: async () => {},
            }),
        []
    );

    return (
        <VideoFeedbackProvider
            project={adaptedProject}
            video={adaptedVideo}
            comments={initialComments}
            revisions={8}
            showFeedbackCriteria
            downloadFileTypes={allowedFileTypes}
            onEditAsComplete={noop}
            onFeedbackSubmit={onFeedbackSubmit}
            onApproved={onRefresh}
            createCommentFn={createCommentFn}
            getCommentsFn={getCommentsFn}
            onApproveFn={onApproveFn}
            onRequestEditFn={onRequestEditFn}
            isPublicPage
            guestName={guestName}
            onGuestNameChange={setGuestName}
        >
            <VideoFeedback
                confirmationData={confirmationData}
                onConfirmationClose={() => setConfirmationData(undefined)}
            />
        </VideoFeedbackProvider>
    );
};

export function PublicVideoReviewSection({
    project,
    token,
    onRefresh,
    publicBase = "brand-orders",
    allowedFileTypes,
}: Props) {
    const files = filterRealFiles(project.projectFiles);
    const { channelGroups, hasChannels } = useChannelGroups(
        project.content_channels || [],
        files
    );

    if (!files.length) {
        return (
            <Box className="flex justify-center items-center my-12">
                <EmptyPanel
                    icon={<IconVideo />}
                    showSpacing
                    text="No videos yet. When the video is ready it will appear here for review."
                />
            </Box>
        );
    }

    return (
        <Box className="flex flex-col gap-6">
            {hasChannels ? (
                channelGroups.map((group, idx) => (
                    <ChannelAccordion
                        key={group.key}
                        group={group}
                        defaultExpanded={idx === 0}
                    >
                        <Box className="flex flex-col gap-8">
                            {group.videos.length > 0 ? (
                                group.videos.map((file: PublicProjectFile) => (
                                    <SingleFileReview
                                        key={file.id}
                                        file={file}
                                        project={project}
                                        token={token}
                                        onRefresh={onRefresh}
                                        allFiles={files}
                                        publicBase={publicBase}
                                        allowedFileTypes={allowedFileTypes}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary" className="py-4 text-center">
                                    No videos for this channel yet.
                                </Typography>
                            )}
                        </Box>
                    </ChannelAccordion>
                ))
            ) : (
                files.map((file) => (
                    <SingleFileReview
                        key={file.id}
                        file={file}
                        project={project}
                        token={token}
                        onRefresh={onRefresh}
                        allFiles={files}
                        publicBase={publicBase}
                        allowedFileTypes={allowedFileTypes}
                    />
                ))
            )}
        </Box>
    );
}
